import { useState, useEffect } from 'react';
import { X, BookOpen, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const SOURCE_SYSTEMS = [
  'SharePoint',
  'OneDrive', 
  'Google Drive',
  'Microsoft 365',
  'Addepar',
  'InfoTrack',
  'Praemium',
  'Xplan',
  'Class Super',
  'BGL',
  'Other'
];

export default function AdviceModal({ document, onClose, onSave }) {
  const [formData, setFormData] = useState({
    title: '',
    type: '',
    adviser: '',
    date: new Date().toISOString().split('T')[0],
    sourceSystem: '',
    reviewDue: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (document) {
      setFormData({
        title: document.title || '',
        type: document.type || '',
        adviser: document.adviser || '',
        date: document.date || new Date().toISOString().split('T')[0],
        sourceSystem: document.sourceSystem || '',
        reviewDue: document.reviewDue || ''
      });
    }
  }, [document]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.adviser.trim()) newErrors.adviser = 'Adviser is required';
    if (!formData.sourceSystem) newErrors.sourceSystem = 'Source system is required';
    if (!formData.date) newErrors.date = 'Date is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSave({
        ...formData,
        reviewDue: formData.reviewDue || null
      });
    }
  };

  const isEditing = !!document;

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-fade-in" 
      data-testid="advice-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="advice-modal-title"
      aria-describedby="advice-modal-description"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#E8E4DF]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#4A3B32]/10 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-[#4A3B32]" />
            </div>
            <div>
              <h2 id="advice-modal-title" className="font-heading text-lg text-[#2C2420]">
                {isEditing ? 'Edit Advice' : 'Add New Advice'}
              </h2>
              <p id="advice-modal-description" className="text-sm text-[#5D524D]">
                {isEditing ? 'Update advice document details.' : 'Add a new advice document record.'}
              </p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onClose}
            className="hover:bg-[#F0EBE6]"
            data-testid="close-advice-modal-btn"
            aria-label="Close dialog"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-[#2C2420] mb-1">
              Title *
            </label>
            <Input
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Enter advice document title"
              className={errors.title ? 'border-red-500' : ''}
              data-testid="advice-title-input"
            />
            {errors.title && (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> {errors.title}
              </p>
            )}
          </div>

          {/* Type & Adviser */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2C2420] mb-1">
                Type
              </label>
              <Input
                value={formData.type}
                onChange={(e) => handleChange('type', e.target.value)}
                placeholder="e.g., SOA, ROA, SoA"
                data-testid="advice-type-input"
              />
              <p className="text-xs text-[#8C8078] mt-1">
                Enter abbreviation (e.g., SOA, ROA)
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2C2420] mb-1">
                Adviser *
              </label>
              <Input
                value={formData.adviser}
                onChange={(e) => handleChange('adviser', e.target.value)}
                placeholder="Adviser name"
                className={errors.adviser ? 'border-red-500' : ''}
                data-testid="advice-adviser-input"
              />
              {errors.adviser && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> {errors.adviser}
                </p>
              )}
            </div>
          </div>

          {/* Source System & Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2C2420] mb-1">
                Source System *
              </label>
              <Select value={formData.sourceSystem} onValueChange={(v) => handleChange('sourceSystem', v)}>
                <SelectTrigger className={errors.sourceSystem ? 'border-red-500' : ''} data-testid="advice-source-select">
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  {SOURCE_SYSTEMS.map(source => (
                    <SelectItem key={source} value={source}>{source}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.sourceSystem && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> {errors.sourceSystem}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2C2420] mb-1">
                Date *
              </label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => handleChange('date', e.target.value)}
                className={errors.date ? 'border-red-500' : ''}
                data-testid="advice-date-input"
              />
              {errors.date && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> {errors.date}
                </p>
              )}
            </div>
          </div>

          {/* Review Due */}
          <div>
            <label className="block text-sm font-medium text-[#2C2420] mb-1">
              Review Due (Optional)
            </label>
            <Input
              type="date"
              value={formData.reviewDue}
              onChange={(e) => handleChange('reviewDue', e.target.value)}
              data-testid="advice-review-input"
            />
            <p className="text-xs text-[#8C8078] mt-1">
              Leave blank if no review is scheduled
            </p>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-[#E8E4DF] bg-[#F9F7F5]">
          <Button variant="outline" onClick={onClose} className="caffeine-btn-secondary">
            Cancel
          </Button>
          <Button onClick={handleSubmit} className="caffeine-btn" data-testid="save-advice-btn">
            {isEditing ? 'Save Changes' : 'Add Advice'}
          </Button>
        </div>
      </div>
    </div>
  );
}
